import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { isStaleChunkError } from '../shared/utils/dynamicImport';

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const __RELEASE_VERSION__: string;
}

/**
 * Reads env vars with fallback runtime (window._env_) → build-time (import.meta.env).
 * Mirrors the helper in src/config/env.ts so Sentry init works identically
 * in production (env.sh writes window._env_) and dev (.env via Vite).
 */
const getEnv = (key: string): string | undefined => {
  if (typeof window !== 'undefined' && window._env_?.[key]) {
    return window._env_[key];
  }
  return (import.meta.env[key] as string | undefined) || undefined;
};

const SENSITIVE_PATH_PREFIXES = [
  '/api/v1/ainovisAI/reports',
  '/api/v1/ainovisAI/stt',
  '/api/v1/ainovisAI/stt-batching',
  '/api/v1/ainovisAI/descriptive',
  '/api/v1/ainovisAI/voice',
  '/api/v1/reports',
  '/api/v1/templates',
  '/api/v1/classifications',
  '/api/v1/drafts',
];

const REDACTED_HEADERS = ['authorization', 'cookie', 'x-csrf-token'];
const REDACTED_QUERY_PARAMS = ['token', 'code'];

function redact(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  // Drop stale-chunk errors: they are expected after a deploy invalidates
  // the previous Vite chunk hashes, and the global handler reloads the page
  // automatically. Reporting them creates noise that masks real errors.
  const exceptionValues = event.exception?.values ?? [];
  for (const ex of exceptionValues) {
    if (isStaleChunkError(`${ex.type ?? ''}: ${ex.value ?? ''}`)) {
      return null;
    }
  }
  if (typeof event.message === 'string' && isStaleChunkError(event.message)) {
    return null;
  }

  const request = event.request;
  if (!request) return event;

  const url = request.url ?? '';
  if (SENSITIVE_PATH_PREFIXES.some((p) => url.includes(p))) {
    delete request.data;
  }

  if (request.headers && typeof request.headers === 'object') {
    const headers = request.headers as Record<string, string>;
    for (const h of REDACTED_HEADERS) {
      if (h in headers) headers[h] = '[Redacted]';
    }
  }

  if (typeof request.query_string === 'string' && request.query_string) {
    const params = new URLSearchParams(request.query_string);
    for (const p of REDACTED_QUERY_PARAMS) {
      if (params.has(p)) params.set(p, '[Redacted]');
    }
    request.query_string = params.toString();
  }

  return event;
}

export function initSentry(): void {
  const dsn = getEnv('VITE_SENTRY_DSN');
  if (!dsn) {
    return;
  }

  const environment = getEnv('VITE_APP_ENV') ?? 'development';
  const release =
    typeof __RELEASE_VERSION__ !== 'undefined' ? __RELEASE_VERSION__ : 'dev';

  Sentry.init({
    dsn,
    environment,
    release,
    sendDefaultPii: false,
    tracesSampleRate: environment === 'production' ? 0.2 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracePropagationTargets: [/^\//, /^https:\/\/api\.ainovis\./],
    beforeSend: redact,
  });
}

export { Sentry };
